// Anonymous "Receipts" analytics beacons fired from public profile pages. No cookies, no auth,
// no PII — these hit the backend's public /a/v1 ingest endpoints directly (NOT apiClient, which
// prefixes /api and attaches a bearer token). Every call is fire-and-forget and never throws, so
// a failed beacon can never disrupt a visitor.

const BACKEND = process.env.REACT_APP_BACKEND_URL
const INGEST = `${BACKEND}/a/v1`

function storageKey(pageId) {
    return `vibio_receipts_${pageId}`
}

// One session key per page per tab (sessionStorage), reused across re-mounts so the backend
// dedups (a repeat start counts as a page view, not a new visit).
function sessionKeyFor(pageId) {
    const key = storageKey(pageId)
    let value = sessionStorage.getItem(key)
    if (!value) {
        value = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`
        sessionStorage.setItem(key, value)
    }
    return value
}

function post(url, body) {
    try {
        fetch(url, {
            method: 'POST',
            headers: body ? { 'Content-Type': 'application/json' } : undefined,
            body: body ? JSON.stringify(body) : undefined,
            keepalive: true, // survives navigation (clicking a link IS navigation)
            mode: 'cors',
            credentials: 'omit',
        }).catch(() => {})
    } catch {
        // ignore — analytics must never break the visit
    }
}

/**
 * Begins a visit: registers the session and starts 5s heartbeats while the tab is visible
 * (the video autoplays, so visible ⇒ watching). Sends a final heartbeat via sendBeacon on
 * hide/unload. Returns a cleanup function to stop everything.
 */
export function startSession({ pageId, videoDurationSeconds = null }) {
    if (!pageId) {
        return () => {}
    }
    const sessionKey = sessionKeyFor(pageId)
    const sourceParam = new URLSearchParams(window.location.search).get('s')

    post(`${INGEST}/sessions`, {
        sessionKey,
        pageId,
        sourceParam: sourceParam || null,
        referrer: document.referrer || null,
        videoDurationSeconds,
    })

    const heartbeatUrl = `${INGEST}/sessions/${sessionKey}/heartbeat`
    const beat = () => {
        if (document.visibilityState === 'visible') {
            post(heartbeatUrl)
        }
    }
    const intervalId = setInterval(beat, 5000)

    // Final flush: sendBeacon (empty body, CORS-simple) survives page unload.
    const flush = () => {
        try {
            navigator.sendBeacon && navigator.sendBeacon(heartbeatUrl)
        } catch {
            // ignore
        }
    }
    const onVisibility = () => {
        if (document.visibilityState === 'hidden') {
            flush()
        }
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
        clearInterval(intervalId)
        window.removeEventListener('pagehide', flush)
        document.removeEventListener('visibilitychange', onVisibility)
    }
}

/**
 * Records a click on a link button or social link. Fired BEFORE navigation; keepalive ensures it
 * is delivered even as the page unloads.
 *
 * @param {string} pageId
 * @param {'LINK_BUTTON'|'SOCIAL_LINK'} targetType
 * @param {string} targetId UUID of the clicked link/social
 */
export function trackClick(pageId, targetType, targetId) {
    if (!pageId || !targetId) {
        return
    }
    const sessionKey = sessionStorage.getItem(storageKey(pageId))
    if (!sessionKey) {
        return
    }
    post(`${INGEST}/sessions/${sessionKey}/clicks`, { targetType, targetId })
}
